import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
    IAumicoCompanySummary,
    ICompanyMappingSuggestion,
} from '../../models/integration/company-mapping-suggestion.model';
import { IAumicoCompanyWithMapping } from '../../models/integration/connection-mapping.model';
import { ConnectionStatus } from '../../models/integration/connection-status.enum';
import { IIntegrationConnection } from '../../models/integration/integration-connection.model';
import { IntegrationVendor } from '../../models/integration/integration-vendor.enum';
import { IExternalCompaniesState } from '../../reducer/integration.reducer';
import { IntegrationProvider } from '../../provider/integration.provider';

/**
 * Manage modal for D365 BC tenants. Three internal views (`ManageView`):
 *
 *  1. `list`               : overview of all connected tenants for this vendor
 *                            with "+ Tenant" CTA.
 *  2. `detail`              : drill down into one tenant; lists BC external companies
 *                            enriched with their currently mapped Aumico Company,
 *                            allows Edit / Unlink per row and Disconnect at tenant scope.
 *  3. `confirm_disconnect`  : two stage confirm before destructive disconnect.
 *
 * Data sources on the detail view:
 *  - `loadExternalCompanies` → `mapping.suggestions` : BC side company list
 *  - `loadMappedCompanies`  → `tenantDetail.aumicoCompanies` : Aumico Companies
 *                                                              linked to this connection
 * Joined client side via `combineLatest`. The mapping payload from the second
 * source decorates each suggestion so when the user clicks Bearbeiten the
 * single edit modal pre selects the currently mapped Aumico Company.
 */
type ManageView = 'list' | 'detail' | 'confirm_disconnect';

interface ITenantRow {
    connection: IIntegrationConnection;
    needsReAuth: boolean;
}

/**
 * Enriched view row: a BC company joined with whichever Aumico Company is
 * currently mapped to it on this connection. `mappedAumicoCompanyId` is what we
 * pass to single edit and Unlink: null means the BC company is not yet mapped.
 */
interface IDetailRow {
    suggestion: ICompanyMappingSuggestion;
    mappedAumicoCompany: IAumicoCompanySummary | null;
    mappedAumicoCompanyId: string | null;
}

@Component({
    selector: 'aumico-d365bc-manage-modal',
    templateUrl: './d365bc-manage-modal.component.html',
    styleUrls: ['./d365bc-manage-modal.component.scss'],
})
export class D365BcManageModalComponent implements OnInit, OnDestroy {
    @Output() public closed = new EventEmitter<void>();
    /** Emitted when the user clicks "+ Neuen Tenant verbinden" inside the list view. */
    @Output() public requestAddTenant = new EventEmitter<void>();

    /**
     * When set on open, drill directly into the detail view for this connection.
     * Used by the post Connect flow to land the user on the auto match result
     * for the freshly created tenant. Read once in ngOnInit; subsequent changes
     * are ignored (the modal user navigates manually after first render).
     */
    @Input() public initialConnectionId: string | null = null;

    public view: ManageView = 'list';
    public tenants$!: Observable<ITenantRow[]>;
    public selectedConnectionId: string | null = null;
    public selectedTenant$: Observable<ITenantRow | null> | null = null;
    public externalCompanies$!: Observable<ICompanyMappingSuggestion[]>;
    public externalCompaniesState$!: Observable<IExternalCompaniesState>;
    public mappedCompanies$!: Observable<IAumicoCompanyWithMapping[]>;
    /**
     * Banner state for the detail view header. Emits null when no auto match
     * has run for the selected tenant; otherwise a discriminated union of
     * running / success / error. Bound via async pipe in the template.
     */
    public autoMatchBanner$: Observable<
        | null
        | { kind: 'running' }
        | { kind: 'error'; message: string }
        | { kind: 'success'; autoMatched: number; notMapped: number; errors: number }
    > | null = null;

    /** Held until the user confirms or cancels the destructive disconnect action. */
    public disconnectCandidate: IIntegrationConnection | null = null;

    public readonly searchTerm$ = new BehaviorSubject<string>('');
    /** Joined view: BC companies enriched with their current Aumico mapping. */
    public detailRows$!: Observable<IDetailRow[]>;

    private readonly destroy$ = new Subject<void>();

    public constructor(private readonly integrationProvider: IntegrationProvider) {}

    public ngOnInit(): void {
        this.tenants$ = this.integrationProvider
            .getConnectionsByVendor$(IntegrationVendor.D365_BC)
            .pipe(
                map((connections) =>
                    connections.map((connection) => ({
                        connection,
                        needsReAuth:
                            connection.status === ConnectionStatus.EXPIRED ||
                            connection.status === ConnectionStatus.ERROR,
                    })),
                ),
            );

        this.externalCompanies$ = this.integrationProvider.getExternalCompanies$();
        this.externalCompaniesState$ = this.integrationProvider.getExternalCompaniesState$();
        this.mappedCompanies$ = this.integrationProvider.getTenantDetailCompanies$();

        // Joined view: every BC suggestion is decorated with its current Aumico
        // mapping (if any). Search term filters by BC company name. The two
        // sources come from disjoint NgRx slices, so opening/closing the
        // single edit modal cannot invalidate this table.
        this.detailRows$ = combineLatest([
            this.externalCompaniesState$,
            this.mappedCompanies$,
            this.searchTerm$,
        ]).pipe(
            map(([external, mapped, term]) => {
                if (external.connectionId !== this.selectedConnectionId) return [];
                const byExternalId = new Map(
                    mapped.map((c) => [c.mapping.externalCompanyId, c]),
                );
                const enriched: IDetailRow[] = external.items.map((s) => {
                    const m = byExternalId.get(s.vendorCompany.externalCompanyId);
                    const mappedAumicoCompany: IAumicoCompanySummary | null = m
                        ? { id: m.id, name: m.name }
                        : null;
                    // Reflect the mapping into the suggestion shape so when the
                    // wizard reads `suggestedAumicoCompany`, it shows the CURRENT
                    // mapping as the "suggested" option for the Confirm button.
                    const decoratedSuggestion: ICompanyMappingSuggestion = {
                        ...s,
                        suggestedAumicoCompany: mappedAumicoCompany ?? s.suggestedAumicoCompany,
                    };
                    return {
                        suggestion: decoratedSuggestion,
                        mappedAumicoCompany,
                        mappedAumicoCompanyId: m?.id ?? null,
                    };
                });
                const trimmed = term.trim().toLowerCase();
                if (!trimmed) return enriched;
                return enriched.filter((row) =>
                    row.suggestion.vendorCompany.externalCompanyName
                        .toLowerCase()
                        .includes(trimmed),
                );
            }),
        );

        // If the last tenant gets removed while the modal is open, bounce back to list.
        // Also: drill directly into the detail view on first emission if
        // `initialConnectionId` was provided (post Connect landing).
        this.tenants$.pipe(takeUntil(this.destroy$)).subscribe((tenants) => {
            if (this.view === 'detail' && this.selectedConnectionId != null) {
                const stillExists = tenants.some(
                    (t) => t.connection.id === this.selectedConnectionId,
                );
                if (!stillExists) {
                    this.view = 'list';
                    this.selectedConnectionId = null;
                    this.selectedTenant$ = null;
                }
                return;
            }
            // First render with an `initialConnectionId` hint: drill in.
            if (
                this.initialConnectionId != null &&
                this.view === 'list' &&
                this.selectedConnectionId == null
            ) {
                const target = tenants.find(
                    (t) => t.connection.id === this.initialConnectionId,
                );
                if (target) this.onSelectTenant(target);
            }
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.searchTerm$.complete();
    }

    // ── Navigation ───────────────────────────────────────────────────────

    public onSelectTenant(row: ITenantRow): void {
        this.selectedConnectionId = row.connection.id;
        this.selectedTenant$ = this.tenants$.pipe(
            map((rows) => rows.find((r) => r.connection.id === row.connection.id) ?? null),
        );
        this.autoMatchBanner$ = this.integrationProvider.getAutoMatchBanner$(row.connection.id);
        this.view = 'detail';
        this.searchTerm$.next('');
        // Load BOTH endpoints in parallel: external (BC) companies for the
        // table + wizard, mapped (Aumico) companies for the join annotation.
        this.integrationProvider.loadExternalCompanies(row.connection.id);
        this.integrationProvider.loadMappedCompanies(row.connection.id);
    }

    public onBackToList(): void {
        this.view = 'list';
        this.selectedConnectionId = null;
        this.selectedTenant$ = null;
        this.searchTerm$.next('');
    }

    public onAddTenant(): void {
        this.requestAddTenant.emit();
    }

    public onEditSingleCompany(row: IDetailRow): void {
        // Open wizard for this BC company. The decoration above means the
        // wizard's "Confirm" button starts pre selected to the current mapping
        // (if any): exactly the "re map this one" UX.
        if (this.selectedConnectionId != null) {
            this.integrationProvider.startSingleEdit(
                this.selectedConnectionId,
                IntegrationVendor.D365_BC,
                row.suggestion,
            );
        }
    }

    public onUnlinkCompany(row: IDetailRow): void {
        // Direct DELETE: no confirm dialog because the action is recoverable
        // (re map via row Edit) and the doc treats this as idempotent. If we
        // want a confirm later, route through `confirm_disconnect` style flow.
        if (row.mappedAumicoCompanyId != null) {
            this.integrationProvider.deleteMapping(
                row.mappedAumicoCompanyId,
                IntegrationVendor.D365_BC,
            );
        }
    }

    public onSearchTermChange(term: string): void {
        this.searchTerm$.next(term);
    }

    // ── Disconnect (destructive, two stage confirm) ──────────────────────

    public onRequestDisconnect(connection: IIntegrationConnection): void {
        this.disconnectCandidate = connection;
        this.view = 'confirm_disconnect';
    }

    public onCancelDisconnect(): void {
        this.disconnectCandidate = null;
        this.view = this.selectedConnectionId != null ? 'detail' : 'list';
    }

    public onConfirmDisconnect(): void {
        if (this.disconnectCandidate == null) return;
        const id = this.disconnectCandidate.id;
        this.integrationProvider.disconnect(id);
        this.disconnectCandidate = null;
        this.view = 'list';
        if (this.selectedConnectionId === id) {
            this.selectedConnectionId = null;
            this.selectedTenant$ = null;
        }
    }

    public onClose(): void {
        this.closed.emit();
    }

    // ── Helpers exposed to template ──────────────────────────────────────

    public trackByConnectionId(_: number, row: ITenantRow): string {
        return row.connection.id;
    }

    public trackByDetailRow(_: number, row: IDetailRow): string {
        return row.suggestion.vendorCompany.externalCompanyId;
    }

    public truncateTenantId(tenantId: string | undefined): string {
        if (!tenantId) return '';
        if (tenantId.length <= 12) return tenantId;
        return `${tenantId.slice(0, 4)}…${tenantId.slice(-4)}`;
    }
}
